/**
 * What we spent on AI models, and who spent it.
 *
 * In production every figure on this screen is zero, and the read itself is
 * not broken: `ai_runs`, the one table the endpoint aggregates, has no rows to
 * attribute. What that zero is not is a measure of spend. The consumer chat
 * records its model calls - and their `total_cost` and `currency` - in
 * `fastapi_interaction_ai_runs` instead, a separate table none of the four
 * queries behind this page touch, so chat spend could never appear here. That
 * table was counted in production too and is also empty, so the omission is
 * real but does not explain the zero - where spend is recorded, if anywhere,
 * is unestablished. The screen says so in as many words: an
 * unexplained zero in front of an operator is how a console gets trusted for
 * something it cannot answer.
 *
 * That fixes the screen's job. It has to distinguish three situations a single
 * total renders identically - nothing recorded at all, calls recorded with no
 * price attached to them, and real attributed spend - and say which one it is
 * looking at. `costState` is that distinction and everything else follows from
 * it.
 *
 * Two rules it must not break. Tone is derived, never passed as a literal:
 * every tile here used to carry a green `success` badge whatever the data said,
 * which teaches an operator that an empty screen is a healthy one. And a total
 * is summed per currency, because the endpoint's own total adds `total_cost`
 * across currencies without converting them - that sum is not an amount of
 * money in any currency, whatever the top-level label happens to say.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { aiCostsApiV1InternalAiCostsGet, type AiCostBreakdown } from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code, Section, StatRow, StatTile } from '@ui/console/primitives';
import { MetricBand } from '@ui/console/MetricBand';
import { Quantity, formatCount, plural } from '@ui/console/measures';
import { RecordTable } from './record-table';
import { MemberPicker } from './references';
import { SelectField } from '@ui/console/forms';
import { InsightState, useInsight } from './consumer-insights';
import { Ranked } from './charts';
import { Filters, Page } from './components';

/** One row of any of the three breakdowns, after the untyped payload is read. */
export type CostRow = {
  id: string;
  label: string;
  currency: string;
  cost: number;
  calls: number;
  raw: Record<string, unknown>;
};

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback;
}

function numeric(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The payload types all three breakdowns as bare dictionaries, so the keys that
 * identify a row differ per breakdown and are named by the caller.
 */
export function readRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  identify: (row: Record<string, unknown>) => string,
): CostRow[] {
  return rows.map((row, index) => {
    const label = identify(row) || `row ${index + 1}`;
    const currency = text(row.currency, 'unknown').toUpperCase();
    return {
      id: `${label}|${currency}`,
      label,
      currency,
      cost: numeric(row.cost),
      calls: numeric(row.calls),
      raw: row,
    };
  });
}

export function featureLabel(row: Record<string, unknown>): string {
  return text(row.feature, 'not recorded');
}

export function modelLabel(row: Record<string, unknown>): string {
  const provider = text(row.provider, 'unknown provider');
  const model = text(row.model, 'unknown model');
  const version = text(row.model_version, '');
  return version && version !== 'provider-resolved'
    ? `${provider} / ${model} @ ${version}`
    : `${provider} / ${model}`;
}

export function userLabel(row: Record<string, unknown>): string {
  return text(row.user_id, 'not recorded');
}

export type CurrencyTotal = { currency: string; cost: number; calls: number };

/** Summed per currency, because adding two currencies together is not a total. */
export function totalsByCurrency(rows: readonly CostRow[]): CurrencyTotal[] {
  const totals = new Map<string, CurrencyTotal>();
  for (const row of rows) {
    const existing = totals.get(row.currency);
    if (existing) {
      existing.cost += row.cost;
      existing.calls += row.calls;
    } else {
      totals.set(row.currency, { currency: row.currency, cost: row.cost, calls: row.calls });
    }
  }
  return [...totals.values()].sort((left, right) => right.cost - left.cost);
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount > 0 && amount < 1 ? 4 : 2,
    }).format(amount);
  } catch {
    // An unrecognised currency code must still render its exact number.
    return `${amount.toFixed(4)} ${currency}`;
  }
}

export type CostState = 'nothing-recorded' | 'calls-without-cost' | 'recorded';

/**
 * Which of three genuinely different situations the payload describes. They
 * look identical if only the total is read, and an operator needs to act
 * differently in each.
 */
export function costState(rows: readonly CostRow[]): CostState {
  if (!rows.length) return 'nothing-recorded';
  if (rows.every((row) => row.cost === 0)) return 'calls-without-cost';
  return 'recorded';
}

function describeTotals(totals: readonly CurrencyTotal[]): string {
  if (!totals.length) return '—';
  return totals.map((row) => formatMoney(row.cost, row.currency)).join(' + ');
}

export function AiCosts() {
  const [params, setParams] = useSearchParams();
  const [rank, setRank] = useState<'calls' | 'cost'>('calls');
  const member = params.get('member') ?? '';
  const source = useInsight<AiCostBreakdown>(() => aiCostsApiV1InternalAiCostsGet(), [], {
    live: false,
  });

  const set = (value: string) => {
    setParams(value ? { member: value } : {}, { replace: true });
  };

  // The band is built from the same read the content is, one level up, so the
  // figures sit above the filters rather than inside the table's own state.
  const ready = source.state.status === 'ready' ? source.state.data : undefined;
  const bandFeatures = ready ? readRows(ready.by_feature, featureLabel) : [];
  const bandUsers = ready ? readRows(ready.by_user, userLabel) : [];
  const bandTotals = totalsByCurrency(bandFeatures);
  const bandCalls = bandTotals.reduce((sum, row) => sum + row.calls, 0);
  const bandUserCalls = bandUsers.reduce((sum, row) => sum + row.calls, 0);
  const bandState = costState(bandFeatures);

  return (
    <Page
      title="AI usage & costs"
      description="Use this page to see how much of our AI model use each product feature, model and member accounts for, and what it cost."
      band={
        <MetricBand
          label="What our model use adds up to"
          figures={[
            {
              id: 'calls',
              label: 'Model calls recorded',
              ...(ready
                ? {
                    value: formatCount(bandCalls),
                    unit: plural(bandCalls, 'call'),
                    raw: bandCalls,
                  }
                : { absent: 'The cost breakdown has not come back yet.' }),
              hint: 'Every recorded call to a paid model, across every feature. This figure is real whether or not a cost was recorded against it.',
            },
            {
              id: 'spend',
              label: 'Total spend',
              ...(!ready
                ? { absent: 'The cost breakdown has not come back yet.' }
                : bandState === 'recorded'
                  ? {
                      value: describeTotals(bandTotals),
                      tone: 'info' as const,
                      statusLabel: 'attributed',
                      hint: `Summed separately per currency. The API's own total adds every currency together without converting, which is why it labels itself "mixed"; it reads ${ready.total}.`,
                    }
                  : {
                      // Not an em dash and not a zero: the figure is a
                      // sentence saying the source is dead, badged so the
                      // deadness is visible before the sentence is read.
                      value: 'not recorded',
                      tone: 'warning' as const,
                      statusLabel: 'nothing writes this',
                      hint: `No cost has been recorded against any call, so there is no amount to show. The API reports a total of ${ready.total} labelled "${ready.currency}", which is a label over an empty sum rather than a measured amount.`,
                    }),
            },
            {
              id: 'members',
              label: 'Members with attributed use',
              ...(ready
                ? {
                    value: formatCount(bandUsers.length),
                    unit: plural(bandUsers.length, 'member'),
                    raw: bandUsers.length,
                    ...(bandUsers.length >= 200
                      ? { tone: 'warning' as const, statusLabel: 'capped at 200' }
                      : {}),
                  }
                : { absent: 'The cost breakdown has not come back yet.' }),
              hint: 'Calls made on behalf of a signed-in member. The API returns the two hundred heaviest, so this stops at 200.',
            },
            {
              id: 'attributed',
              label: 'Calls attributed to a member (up to 200 members)',
              ...(ready
                ? {
                    value: formatCount(bandUserCalls),
                    unit: plural(bandUserCalls, 'call'),
                    raw: bandUserCalls,
                    ...(bandCalls > 0
                      ? {
                          bar: {
                            fraction: bandUserCalls / bandCalls,
                            label: `Attributed to a member: ${bandUserCalls} of ${bandCalls} calls`,
                            tone: 'info' as const,
                          },
                        }
                      : {}),
                  }
                : { absent: 'The cost breakdown has not come back yet.' }),
              hint:
                bandUsers.length >= 200
                  ? 'Counted over the 200 members the API returns. The rest were made for members past that limit, or by background work with no member attached, such as scheduled generation.'
                  : 'The rest were made by background work with no member attached, such as scheduled generation.',
            },
          ]}
        />
      }
      action={
        <Button size="md" color="secondary" onClick={() => void source.refresh()}>
          Refresh
        </Button>
      }
    >
      <InsightState source={source} subject="AI usage and costs">
        {(breakdown) => {
          const byFeature = readRows(breakdown.by_feature, featureLabel);
          const byModel = readRows(breakdown.by_model, modelLabel);
          const byUser = readRows(breakdown.by_user, userLabel);
          // Every run carries a feature, so this breakdown is the whole
          // population; the other two are subsets of it and cannot be the
          // denominator.
          const totals = totalsByCurrency(byFeature);
          const state = costState(byFeature);
          const totalCalls = totals.reduce((sum, row) => sum + row.calls, 0);
          const users = member ? byUser.filter((row) => row.label === member) : byUser;
          const rankRows = (rows: readonly CostRow[], denominator: number) => ({
            rows: rows.map((row) => ({
              id: row.id,
              label: row.label,
              count: rank === 'cost' ? row.cost : row.calls,
              trailing:
                rank === 'cost'
                  ? `${formatCount(row.calls)} calls`
                  : row.cost > 0
                    ? formatMoney(row.cost, row.currency)
                    : undefined,
            })),
            total: denominator,
          });

          return (
            <div className="flex flex-col gap-6">
              {state === 'calls-without-cost' ? (
                <Callout
                  tone="warning"
                  title="Model calls are being recorded. Their cost is not, so every money figure below is a true zero."
                >
                  <p>
                    Each call is written down with its feature, its model, its tokens and the member
                    it was for, and the counts on this page are real. The amount is taken straight
                    from whatever the model provider volunteers in its response, we never ask the
                    provider to report one, and nothing prices the call on our side &mdash; so the
                    cost column is written as zero on every call and always has been.
                  </p>
                  <p className="pt-2">
                    The per-model prices collected on the Model registry screen are stored and read
                    by nothing. Until either the provider is asked for a cost or those stored prices
                    are applied to the recorded tokens, this page can report usage but not spend.
                    That is a change to the product API, not to this screen.
                  </p>
                </Callout>
              ) : null}

              {/* The detail an operator does not need but a maintainer does: this
                  page aggregates `ai_runs` and nothing else, and that table is
                  empty in production. Consumer chat writes its runs, with
                  `total_cost` and `currency`, to `fastapi_interaction_ai_runs`,
                  which none of the four queries here touch - so chat spend could
                  never appear. That table was counted too and is also empty, so
                  reading both is still the right fix but would not change a
                  figure today. Where paid-model spend is recorded, if anywhere,
                  is unresolved. */}
              {state === 'nothing-recorded' ? (
                <Callout
                  tone="warning"
                  title="No model calls are recorded, so this is not a spend figure."
                >
                  <p>
                    The read succeeded and came back empty: no calls at all, rather than calls
                    costing nothing. The product does call paid models, so treat every figure on
                    this page as unknown rather than as nought.
                  </p>
                </Callout>
              ) : null}

              <StatRow>
                <StatTile
                  label="Product features using AI"
                  value={
                    <Quantity
                      value={formatCount(new Set(byFeature.map((row) => row.label)).size)}
                      unit={plural(new Set(byFeature.map((row) => row.label)).size, 'feature')}
                      raw={new Set(byFeature.map((row) => row.label)).size}
                    />
                  }
                  hint="Distinct features that have made at least one model call."
                />
                <StatTile
                  label="Model versions called"
                  value={
                    <Quantity
                      value={formatCount(new Set(byModel.map((row) => row.label)).size)}
                      unit={plural(new Set(byModel.map((row) => row.label)).size, 'model')}
                      raw={new Set(byModel.map((row) => row.label)).size}
                    />
                  }
                  hint="Each provider, model and version counted separately, because they are priced separately."
                />
              </StatRow>

              {totals.length > 1 ? (
                <Section
                  title="Spend by currency"
                  description="Kept apart rather than added up. The API's single total adds these together without converting them, so it is not an amount of money in any currency."
                >
                  <RecordTable
                    noun="currency totals"
                    records={totals.map((row) => ({
                      currency: row.currency,
                      cost: formatMoney(row.cost, row.currency),
                      calls: row.calls,
                    }))}
                    columns={['currency', 'cost', 'calls']}
                  />
                </Section>
              ) : null}

              <Filters>
                <SelectField
                  label="Rank by"
                  value={rank}
                  onChange={(event) =>
                    setRank(event.currentTarget.value === 'cost' ? 'cost' : 'calls')
                  }
                  hint={
                    state === 'recorded'
                      ? 'Cost and call volume rank differently: one expensive model can outweigh a chatty cheap one.'
                      : 'Cost is zero on every row, so ranking by it puts everything level. Call volume is the figure with content in it.'
                  }
                  options={[
                    { value: 'calls', label: 'Number of calls' },
                    { value: 'cost', label: 'Cost' },
                  ]}
                />
              </Filters>

              <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
                <Section
                  title="By product feature"
                  description="Which part of the product is doing the asking. Every recorded call has a feature, so these add up to the whole."
                >
                  <Ranked
                    {...rankRows(
                      byFeature,
                      rank === 'cost' ? totals.reduce((sum, row) => sum + row.cost, 0) : totalCalls,
                    )}
                    unit={rank === 'cost' ? 'cost' : 'calls'}
                    emptyHint="No call has been recorded against any feature."
                  />
                </Section>

                <Section
                  title="By model"
                  description="Provider, model and version, counted apart. A version change is a price change, so they are never merged."
                >
                  <Ranked
                    {...rankRows(
                      byModel,
                      rank === 'cost' ? totals.reduce((sum, row) => sum + row.cost, 0) : totalCalls,
                    )}
                    unit={rank === 'cost' ? 'cost' : 'calls'}
                    emptyHint="No call has been recorded against any model."
                  />
                </Section>
              </div>

              <Section
                title="By member"
                description="Calls made on behalf of one signed-in member. The API returns the two hundred heaviest and no more, so this is not the whole population."
              >
                <Filters>
                  <MemberPicker
                    label="Find a member"
                    value={member}
                    onChange={set}
                    hint="Search by name or email; only members among the rows returned can match."
                  />
                </Filters>
                {users.length ? (
                  <RecordTable
                    noun="members"
                    records={users.map((row) => ({
                      user_id: row.label,
                      user_name:
                        typeof row.raw['user_name'] === 'string' ? row.raw['user_name'] : null,
                      calls: row.calls,
                      cost: row.cost > 0 ? formatMoney(row.cost, row.currency) : 'not recorded',
                      currency: row.currency,
                    }))}
                    columns={['user_id', 'calls', 'cost', 'currency']}
                  />
                ) : (
                  <Callout
                    tone="neutral"
                    title={
                      member
                        ? 'That member is not among the returned rows.'
                        : 'No call has been attributed to a member.'
                    }
                  >
                    {member
                      ? 'Only the two hundred heaviest members are returned, so a quieter member will not appear here even though their calls are recorded.'
                      : 'Either nothing has run for a signed-in member, or every recorded call was background work with no member attached. The read succeeded either way.'}
                  </Callout>
                )}
              </Section>

              <Callout tone="neutral" title="What this page counts">
                Every recorded call is counted, including calls that failed, so a feature that is
                failing every attempt still shows its calls here. The member breakdown stops at{' '}
                <Code>200</Code> rows by the API&rsquo;s own limit, and there is no date range: this
                is everything ever recorded, not a period.
              </Callout>
            </div>
          );
        }}
      </InsightState>
    </Page>
  );
}
